'use client'

import * as React from 'react'
import { LuBox, LuCheck, LuChevronDown, LuChevronRight, LuSearch, LuMousePointer } from 'react-icons/lu'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { useModels } from '@/data/hooks/api'
import type { ModelGroup } from '@/data/hooks/api/usePricing'
import { ProviderIcon } from '@/components/provider-pill'
import type { ModelSelection } from '@/data/types/common.types'

interface ModelsSelectorProps {
  value?: ModelSelection
  onChange?: (selection: ModelSelection) => void
  className?: string
}

// Default empty selection
export const NO_MODELS_SELECTION: ModelSelection = { all: false, providers: [], models: [] }
// Default all selection
export const ALL_MODELS_SELECTION: ModelSelection = { all: true, providers: [], models: [] }

const ModelsSelector = ({
  value: externalValue,
  onChange: externalOnChange,
  className,
}: ModelsSelectorProps) => {
  const [open, setOpen] = React.useState(false)
  const [manuallyExpandedGroups, setManuallyExpandedGroups] = React.useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = React.useState('')
  const [hoveredModel, setHoveredModel] = React.useState<string | null>(null)
  const [hoveredProvider, setHoveredProvider] = React.useState<string | null>(null)

  // Internal state for uncontrolled mode
  const [internalValue, setInternalValue] = React.useState<ModelSelection>(ALL_MODELS_SELECTION)
  // Track if initial load has happened (to auto-select all on first load only)
  const hasInitialized = React.useRef(false)

  // Use external value if provided, otherwise use internal state
  const value = externalValue !== undefined ? externalValue : internalValue
  const onChange = externalOnChange || setInternalValue

  // Fetch models using React Query
  const { data: modelGroupsData = [], isLoading } = useModels()
  const modelGroups = modelGroupsData

  // Select all models by default when data loads ONLY on first load
  React.useEffect(() => {
    if (!isLoading && modelGroups.length > 0 && externalValue === undefined && !hasInitialized.current) {
      hasInitialized.current = true
      setInternalValue(ALL_MODELS_SELECTION)
    }
  }, [isLoading, modelGroups, externalValue])

  // Calculate all models flat array
  const allModels = React.useMemo(
    () => modelGroups.flatMap((g) => g.models),
    [modelGroups]
  )

  // Expand the ModelSelection to get the actual selected models list
  const expandedSelectedModels = React.useMemo(() => {
    if (value.all) {
      return allModels
    }
    const models: Set<string> = new Set(value.models)
    // Add all models from selected providers
    for (const provider of value.providers) {
      const group = modelGroups.find((g) => g.provider.toLowerCase() === provider.toLowerCase())
      if (group) {
        group.models.forEach((m) => models.add(m))
      }
    }
    return Array.from(models)
  }, [value, modelGroups, allModels])

  // Filter groups based on search query
  const filteredGroups = React.useMemo(() => {
    if (!searchQuery.trim()) return modelGroups

    const query = searchQuery.toLowerCase()
    return modelGroups
      .map((group) => ({
        ...group,
        models: group.models.filter(
          (model) =>
            model.toLowerCase().includes(query) ||
            group.provider.toLowerCase().includes(query)
        ),
      }))
      .filter((group) => group.models.length > 0)
  }, [modelGroups, searchQuery])

  // Check selection states
  const isAllSelected = value.all || (allModels.length > 0 && expandedSelectedModels.length === allModels.length)
  const isNoneSelected = !value.all && value.providers.length === 0 && value.models.length === 0
  const isSomeSelected = !isNoneSelected && !isAllSelected
  const selectedCount = expandedSelectedModels.length

  // Get display text for the button
  const getDisplayText = () => {
    if (isLoading) return 'Loading...'
    if (isAllSelected) return 'All Models'
    if (isNoneSelected) return 'No Models'
    // Check if it's a single provider
    if (value.providers.length === 1 && value.models.length === 0) {
      return `All ${getProviderDisplayName(value.providers[0])}`
    }
    return `${selectedCount} Model${selectedCount === 1 ? '' : 's'}`
  }

  // Determine if a group should be expanded
  const isGroupExpanded = (provider: string, selectionState: 'none' | 'partial' | 'all') => {
    // Always expand when searching
    if (searchQuery.length > 0) return true

    // If manually toggled, respect that
    if (manuallyExpandedGroups.has(provider)) return true

    // Collapse when all selected
    if (isAllSelected) return false

    // Expand when none selected (so user can pick)
    if (isNoneSelected) return true

    // Expand if there's partial selection in this group
    if (selectionState === 'partial') return true

    // Otherwise collapsed
    return false
  }

  // Toggle group expansion manually
  const toggleGroupExpansion = (provider: string) => {
    setManuallyExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(provider)) {
        next.delete(provider)
      } else {
        next.add(provider)
      }
      return next
    })
  }

  // Get group selection state
  const getGroupSelectionState = (group: ModelGroup): 'none' | 'partial' | 'all' => {
    // Check if this provider is selected
    if (value.all) return 'all'
    if (value.providers.includes(group.provider.toLowerCase())) return 'all'
    
    const selectedInGroup = group.models.filter((m) => expandedSelectedModels.includes(m)).length
    if (selectedInGroup === 0) return 'none'
    if (selectedInGroup === group.models.length) return 'all'
    return 'partial'
  }

  // Select/deselect all models in a group
  const toggleGroupSelection = (group: ModelGroup, e?: React.MouseEvent) => {
    e?.stopPropagation()
    const state = getGroupSelectionState(group)
    if (state === 'all') {
      // Deselect - remove provider and any individual models from this group
      const newProviders = value.providers.filter((p) => p.toLowerCase() !== group.provider.toLowerCase())
      const newModels = value.models.filter((m) => !group.models.includes(m))
      onChange({ all: false, providers: newProviders, models: newModels })
    } else {
      // Select all in this group - add provider
      const newProviders = [...new Set([...value.providers, group.provider.toLowerCase()])]
      // Remove individual models from this group since provider is now selected
      const newModels = value.models.filter((m) => !group.models.includes(m))
      onChange({ all: false, providers: newProviders, models: newModels })
    }
  }

  // Toggle individual model selection
  const toggleModel = (model: string, group: ModelGroup, e?: React.MouseEvent) => {
    e?.stopPropagation()
    const isSelected = expandedSelectedModels.includes(model)
    
    if (value.all) {
      // If all is selected, switching to selecting all except this one is complex
      // Instead, switch to provider-based + individual models
      const otherModels = allModels.filter((m) => m !== model)
      onChange({ all: false, providers: [], models: otherModels })
      return
    }
    
    // Check if this model's provider is fully selected
    const isProviderSelected = value.providers.includes(group.provider.toLowerCase())
    
    if (isProviderSelected) {
      // Need to "break apart" the provider selection
      // Remove provider, add all other models from this provider individually
      const newProviders = value.providers.filter((p) => p.toLowerCase() !== group.provider.toLowerCase())
      const otherModelsFromProvider = group.models.filter((m) => m !== model)
      const newModels = [...new Set([...value.models, ...otherModelsFromProvider])]
      onChange({ all: false, providers: newProviders, models: newModels })
    } else if (isSelected) {
      // Just remove this model
      const newModels = value.models.filter((m) => m !== model)
      onChange({ all: false, providers: value.providers, models: newModels })
    } else {
      // Add this model
      const newModels = [...value.models, model]
      // Check if this completes the provider - if so, consolidate to provider
      const providerModelsSelected = group.models.filter((m) => newModels.includes(m) || value.providers.includes(group.provider.toLowerCase()))
      if (providerModelsSelected.length === group.models.length) {
        // All models in provider selected - consolidate to provider
        const newProviders = [...new Set([...value.providers, group.provider.toLowerCase()])]
        const modelsWithoutProvider = newModels.filter((m) => !group.models.includes(m))
        onChange({ all: false, providers: newProviders, models: modelsWithoutProvider })
      } else {
        onChange({ all: false, providers: value.providers, models: newModels })
      }
    }
  }

  // Select ONLY this model (exclusive selection)
  const selectOnlyModel = (model: string, e?: React.MouseEvent) => {
    e?.stopPropagation()
    onChange({ all: false, providers: [], models: [model] })
  }

  // Select ONLY this provider (exclusive selection)
  const selectOnlyProvider = (provider: string, e?: React.MouseEvent) => {
    e?.stopPropagation()
    onChange({ all: false, providers: [provider.toLowerCase()], models: [] })
  }

  // Toggle Select All - if all selected, clear all; otherwise select all
  const toggleSelectAll = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    if (isAllSelected) {
      onChange(NO_MODELS_SELECTION)
    } else {
      onChange(ALL_MODELS_SELECTION)
    }
    setManuallyExpandedGroups(new Set())
  }

  // Clear all selections - FIX: don't reset manuallyExpandedGroups
  const clearAll = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    onChange(NO_MODELS_SELECTION)
    // Don't reset manuallyExpandedGroups - this fixes the bug where clear expands collapsed providers
  }

  // Get proper display name for provider
  const getProviderDisplayName = (provider: string) => {
    const lowerProvider = provider?.toLowerCase() || ''
    if (lowerProvider === 'openai') return 'OpenAI'
    if (lowerProvider === 'google') return 'Google'
    if (lowerProvider === 'anthropic') return 'Anthropic'
    if (lowerProvider === 'claude') return 'Claude'
    return provider.charAt(0).toUpperCase() + provider.slice(1)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="transparent-outline"
          size="xs"
          className={cn(
            'hover:bg-[#F2EEFF] hover:text-[#654EDA] hover:border-[#E2DAFF]',
            'focus-visible:ring-[#654EDA]/20 focus-visible:border-[#654EDA] focus-visible:ring-2',
            open && 'bg-[#E2DAFF] text-[#654EDA] border-[#C6B9FF] hover:bg-[#E2DAFF] focus-visible:ring-0',
            className
          )}
        >
          <LuBox className="size-4" />
          {getDisplayText()}
          <LuChevronDown
            className={cn(
              'size-3.5 ml-0.5 transition-transform duration-200',
              open && 'rotate-180'
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[320px] p-0 border-[#EDEEF3] shadow-lg flex flex-col max-h-[var(--radix-popover-content-available-height,400px)]"
        align="start"
        sideOffset={6}
      >
        {/* Header with Search and Clear */}
        <div className="p-3 border-b border-[#EDEEF3] shrink-0">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[#8A8F98]" />
              <input
                type="text"
                placeholder="Search models..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-[30px] pl-9 pr-3 rounded-[6px] border border-[#EDEEF3] bg-white text-[13px] text-[#08090A] placeholder:text-[#8A8F98] hover:border-[#D1D5DB] focus:outline-none focus:border-[#654EDA] transition-colors"
              />
            </div>
            {/* Clear button - shown when there are selections */}
            {!isLoading && selectedCount > 0 && (
              <button
                type="button"
                onClick={clearAll}
                className="text-[12px] text-[#8A8F98] hover:text-[#654EDA] transition-colors whitespace-nowrap cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 min-h-0" onWheel={(e) => e.stopPropagation()}>
          {isLoading ? (
            <div className="p-4 text-center text-[13px] text-[#8A8F98]">
              Loading models...
            </div>
          ) : modelGroups.length === 0 ? (
            <div className="p-4 text-center text-[13px] text-[#8A8F98]">
              No models available
            </div>
          ) : filteredGroups.length === 0 ? (
            <div className="p-4 text-center text-[13px] text-[#8A8F98]">
              No models found
            </div>
          ) : (
            <>
              {/* Select All Option */}
              {!searchQuery && (
                <div
                  className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#F7F8F8] transition-colors border-b border-[#EDEEF3]"
                  onClick={toggleSelectAll}
                >
                  <Checkbox
                    checked={isAllSelected ? true : isSomeSelected ? "indeterminate" : false}
                    onCheckedChange={() => toggleSelectAll()}
                    className="size-4 border-[#D1D5DB] data-[state=checked]:bg-[#654EDA] data-[state=checked]:border-[#654EDA] data-[state=indeterminate]:bg-[#654EDA] data-[state=indeterminate]:border-[#654EDA] cursor-pointer"
                  />
                  <span className="text-[13px] font-medium text-[#08090A]">
                    Select All
                  </span>
                  <span className="ml-auto text-[12px] text-[#8A8F98]">
                    {selectedCount}/{allModels.length}
                  </span>
                </div>
              )}

              {/* Provider Groups */}
              {filteredGroups.map((group) => {
                const selectionState = getGroupSelectionState(group)
                const isExpanded = isGroupExpanded(group.provider, selectionState)
                const selectedInGroup = group.models.filter((m) => expandedSelectedModels.includes(m)).length
                const isProviderHovered = hoveredProvider === group.provider

                return (
                  <div key={group.provider} className="border-b border-[#EDEEF3] last:border-b-0">
                    {/* Provider Header */}
                    <div 
                      className="flex items-center"
                      onMouseEnter={() => setHoveredProvider(group.provider)}
                      onMouseLeave={() => setHoveredProvider(null)}
                    >
                      {/* Checkbox area - clickable for selection */}
                      <div
                        className="flex items-center gap-3 px-3 py-2.5 flex-1 cursor-pointer hover:bg-[#F7F8F8] transition-colors"
                        onClick={(e) => toggleGroupSelection(group, e)}
                      >
                        <Checkbox
                          checked={selectionState === 'all' ? true : selectionState === 'partial' ? "indeterminate" : false}
                          onCheckedChange={() => toggleGroupSelection(group)}
                          className="size-4 border-[#D1D5DB] data-[state=checked]:bg-[#654EDA] data-[state=checked]:border-[#654EDA] data-[state=indeterminate]:bg-[#654EDA] data-[state=indeterminate]:border-[#654EDA] cursor-pointer"
                        />
                        <ProviderIcon provider={group.provider} className="size-4" />
                        <span className="text-[13px] font-medium text-[#08090A]">
                          {getProviderDisplayName(group.provider)}
                        </span>
                        
                        {/* Select Only button - shown on hover */}
                        {isProviderHovered && !searchQuery && (
                          <button
                            type="button"
                            onClick={(e) => selectOnlyProvider(group.provider, e)}
                            className="ml-auto text-[11px] text-[#8A8F98] hover:text-[#654EDA] transition-colors flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-[#F2EEFF]"
                            title="Select only this provider"
                          >
                            <LuMousePointer className="size-3" />
                            Only
                          </button>
                        )}
                        
                        {!isProviderHovered && (
                          <span className="ml-auto text-[12px] text-[#8A8F98]">
                            {selectedInGroup}/{group.models.length}
                          </span>
                        )}
                      </div>

                      {/* Expand/Collapse button */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleGroupExpansion(group.provider)
                        }}
                        className="px-3 py-2.5 hover:bg-[#F7F8F8] transition-colors"
                      >
                        {isExpanded ? (
                          <LuChevronDown className="size-4 text-[#8A8F98]" />
                        ) : (
                          <LuChevronRight className="size-4 text-[#8A8F98]" />
                        )}
                      </button>
                    </div>

                    {/* Models List */}
                    {isExpanded && (
                      <div className="ml-4 border-l border-[#EDEEF3]">
                        {group.models.map((model) => {
                          const isSelected = expandedSelectedModels.includes(model)
                          const isModelHovered = hoveredModel === model
                          return (
                            <div
                              key={model}
                              className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#F7F8F8] transition-colors"
                              onClick={(e) => toggleModel(model, group, e)}
                              onMouseEnter={() => setHoveredModel(model)}
                              onMouseLeave={() => setHoveredModel(null)}
                            >
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleModel(model, group)}
                                className="size-4 border-[#D1D5DB] data-[state=checked]:bg-[#654EDA] data-[state=checked]:border-[#654EDA]"
                              />
                              <span className="text-[13px] text-[#08090A] truncate flex-1">
                                {model}
                              </span>
                              
                              {/* Select Only button - shown on hover */}
                              {isModelHovered && (
                                <button
                                  type="button"
                                  onClick={(e) => selectOnlyModel(model, e)}
                                  className="text-[11px] text-[#8A8F98] hover:text-[#654EDA] transition-colors flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-[#F2EEFF] shrink-0"
                                  title="Select only this model"
                                >
                                  <LuMousePointer className="size-3" />
                                  Only
                                </button>
                              )}
                              
                              {!isModelHovered && isSelected && (
                                <LuCheck className="ml-auto size-4 text-[#654EDA] shrink-0" />
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { ModelsSelector }
export type { ModelSelection }
